// Launch glyphs (contracts/launch-types.md §1): glyph.cube@1 and glyph.clue@1, each one static SVG.
import { shapePath } from "../geometry";
import { puzzleColor } from "../glyphs";
import type { ClueGlyph, CubeFace, CubeGlyph, PuzzleColor, ShapeGlyph, TextGlyph } from "../types";
import { kindOf } from "../types";
import { cubeFaces } from "./geometry";
import styles from "./launch.module.css";

/** Plain colour words for accessible names ("Cube: top red star, …"). */
const COLOR_NAMES: Record<string, string> = {
  ink: "white",
  accent: "yellow",
  ember: "coral",
  violet: "violet",
  mint: "mint",
  sky: "sky blue",
  pink: "pink",
  amber: "amber",
  orange: "orange",
  red: "red",
  blue: "blue",
  purple: "purple",
  yellow: "yellow",
  green: "green",
  muted: "grey",
};

export const colorName = (c: PuzzleColor | string | undefined) => COLOR_NAMES[String(c ?? "ink")] ?? "white";

function symbolName(symbol: CubeFace["symbol"]): string {
  if (kindOf(symbol) === "text") return String((symbol as TextGlyph).value ?? "").trim();
  if (kindOf(symbol) === "shape") {
    const s = symbol as ShapeGlyph;
    return `${s.fill === "outline" ? "hollow " : ""}${String(s.shape ?? "shape")}`;
  }
  return "";
}

/** "top red star", "front white 2", "right mint" (no symbol). */
export const faceLabel = (name: string, face: CubeFace | undefined) =>
  [name, colorName(face?.color), symbolName(face?.symbol)].filter(Boolean).join(" ");

/** "Cube: top red star, front blue circle, right green triangle". Never more than what is drawn. */
export const cubeLabel = (g: CubeGlyph) => `Cube: ${(["top", "front", "right"] as const).map((f) => faceLabel(f, g[f])).join(", ")}`;

/** Symbols are dark on the face (the net and the die, board 15 S3/S6) unless the symbol names its own colour. */
const symbolInk = (color: PuzzleColor | undefined) => (!color || color === "ink" ? "var(--color-on-white-ink)" : puzzleColor(color));

function FaceSymbol({ symbol }: { symbol: CubeFace["symbol"] }) {
  if (kindOf(symbol) === "text") {
    const t = symbol as TextGlyph;
    const value = String(t.value ?? "").trim();
    // Large: a die's numbers must read at tile size (board 15 S6).
    const size = value.length <= 1 ? 76 : value.length === 2 ? 54 : 36;
    return (
      <text x={50} y={52} className={styles.cubeText} fontSize={size} style={{ fill: symbolInk(t.color) }}>
        {value}
      </text>
    );
  }
  if (kindOf(symbol) !== "shape") return null;
  const s = symbol as ShapeGlyph;
  const scale = Math.min(1, Math.max(0.2, typeof s.scale === "number" ? s.scale : 1));
  const path = shapePath(s.shape, 27 * scale);
  if (!path) return null;
  const ink = symbolInk(s.color);
  const rotation = typeof s.rotation === "number" ? s.rotation : 0;
  const outline = s.fill === "outline" || s.shape === "ring";
  return (
    <g transform={`rotate(${rotation} 50 50)${s.mirrored ? " matrix(-1 0 0 1 100 0)" : ""}`}>
      <path
        d={path.d}
        fillRule={path.evenOdd ? "evenodd" : "nonzero"}
        style={outline && s.shape !== "ring" ? { fill: "none", stroke: ink, strokeWidth: 7, strokeLinejoin: "round" } : { fill: ink }}
      />
    </g>
  );
}

const FACES = cubeFaces();

/**
 * glyph.cube@1: an isometric cube (the glyph.iso@1 projection) with its top, front (lower left) and right
 * (lower right) faces in their token colours, dark edges, and each face's symbol mapped onto the face by
 * its affine transform, so it is skewed with the face as on board 15 S3. One static SVG.
 */
export function CubeView({ glyph }: { glyph: CubeGlyph }) {
  return (
    <svg className={styles.glyphSvg} viewBox="0 0 100 100" role="img" aria-label={cubeLabel(glyph)} data-cube>
      {FACES.map((f) => {
        const face = glyph[f.face];
        return (
          <g key={f.face} data-face={f.face}>
            <polygon points={f.points} className={styles.cubeFace} style={{ fill: puzzleColor(face?.color) }} />
            {face?.symbol ? (
              <g transform={f.transform}>
                <FaceSymbol symbol={face.symbol} />
              </g>
            ) : null}
          </g>
        );
      })}
    </svg>
  );
}

/** "Clue: across 3, down 11" (or one of them). */
export function clueLabel(g: ClueGlyph): string {
  const parts = [
    typeof g.across === "number" ? `across ${g.across}` : "",
    typeof g.down === "number" ? `down ${g.down}` : "",
  ].filter(Boolean);
  return parts.length ? `Clue: ${parts.join(", ")}` : "Clue square";
}

/**
 * glyph.clue@1 (Kakuro, board 14 N9): a square split by a diagonal, the across sum top-right and the down
 * sum bottom-left, in mono. Fills its container (a grid cell draws the dark clue tile behind it).
 */
export function ClueView({ glyph }: { glyph: ClueGlyph }) {
  const across = typeof glyph.across === "number" ? String(glyph.across) : "";
  const down = typeof glyph.down === "number" ? String(glyph.down) : "";
  return (
    <svg className={styles.glyphSvg} viewBox="0 0 100 100" preserveAspectRatio="none" role="img" aria-label={clueLabel(glyph)} data-clue>
      <line x1={4} y1={96} x2={96} y2={4} className={styles.clueLine} vectorEffect="non-scaling-stroke" />
      {across ? (
        <text x={90} y={14} className={styles.clueText} textAnchor="end" dominantBaseline="hanging">
          {across}
        </text>
      ) : null}
      {down ? (
        <text x={10} y={88} className={styles.clueText} textAnchor="start">
          {down}
        </text>
      ) : null}
    </svg>
  );
}
